import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listActiveMediosDePago } from '@/shared/api/controllers/mediosDePago';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';

/**
 * ¿La caja opera en dólares? Lo define el ABM de Medios de Pago (toggle
 * "Caja en dólares" = Categoria_MDP 'Internacional'), no el nombre. Espejo de
 * server/src/lib/cajaMoneda.ts.
 *
 * `esCajaUsd` es sincrónica para los helpers que sólo reciben el nombre; el
 * componente que la usa llama `useEsCajaUsd()` para cargar las cajas y
 * volver a renderizar cuando llegan.
 */
let usdPorNombre = new Map<string, boolean>();

const clave = (nombre: string) => nombre.trim().toLowerCase();

export function esCajaUsd(caja: string | null | undefined): boolean {
  const nombre = (caja ?? '').trim();
  if (!nombre) return false;
  const v = usdPorNombre.get(clave(nombre));
  if (v !== undefined) return v;
  // ponytail: caja inactiva / todavía sin cargar → criterio viejo por nombre.
  return /\bd[oó]lar\b|\busd\b/i.test(nombre);
}

export function useEsCajaUsd(): (caja: string | null | undefined) => boolean {
  const { config } = useActiveCountry();
  const { data } = useQuery({
    queryKey: ['tesoreria', config.code, 'medios'],
    queryFn: listActiveMediosDePago,
    staleTime: 5 * 60_000,
  });
  // Nueva identidad cuando cambian las cajas: los useMemo que dependen de
  // esta función se recalculan.
  return useMemo(() => {
    if (data) {
      usdPorNombre = new Map(
        data.map((m) => [clave(m.fields.Medio_MDP ?? ''), m.fields.Categoria_MDP === 'Internacional']),
      );
    }
    return (caja: string | null | undefined) => esCajaUsd(caja);
  }, [data]);
}
